import {
  generateOccurrences,
  MAX_OCCURRENCE_LIMIT,
  SweepstakesRulesInput,
  TicketingFormCategory,
  TicketingRecurrenceType,
} from "@simplyk/common";

export const isSweepstakesCategory = (category: unknown): category is TicketingFormCategory.Sweepstakes =>
  category === TicketingFormCategory.Sweepstakes;

export const usesEntryTerminology = (category: unknown): boolean => isSweepstakesCategory(category);

const DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000;
const WINNERS_LIST_REQUEST_DELAY_IN_DAYS = 30;

const parseRulesDate = (date: Date | string | null | undefined): Date | null => {
  if (!date) {
    return null;
  }
  const parsedDate = date instanceof Date ? date : new Date(date);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const formatRulesDate = (date: Date, timeZone: string, options: Intl.DateTimeFormatOptions): string => {
  try {
    return date.toLocaleString("en-US", { ...options, timeZone });
  } catch {
    return date.toLocaleString("en-US", { ...options, timeZone: "UTC" });
  }
};

const formatSweepstakesRulesDate = (date: Date | string | null | undefined, timeZone: string): string | null => {
  const parsedDate = parseRulesDate(date);
  return parsedDate && formatRulesDate(parsedDate, timeZone, { dateStyle: "long", timeStyle: "short" });
};

const formatSweepstakesRulesDay = (date: Date | string | null | undefined, timeZone: string): string | null => {
  const parsedDate = parseRulesDate(date);
  return parsedDate && formatRulesDate(parsedDate, timeZone, { dateStyle: "long" });
};

const addDays = (date: Date, days: number): Date => new Date(date.getTime() + days * DAY_IN_MILLISECONDS);

interface SweepstakesDrawRecurrence {
  type?: TicketingRecurrenceType | null;
  startUtc: Date | string;
  endUtc: Date | string;
  repeatUntilDate?: Date | string | null;
}

const getSweepstakesDrawingStarts = (
  recurrences: readonly SweepstakesDrawRecurrence[] | null | undefined,
  timeZone: string
): Date[] =>
  (recurrences ?? [])
    .flatMap((recurrence) =>
      generateOccurrences({
        recurrence: {
          type: recurrence.type || TicketingRecurrenceType.Once,
          startUtc: new Date(recurrence.startUtc),
          endUtc: new Date(recurrence.endUtc),
          repeatUntilDate: recurrence.repeatUntilDate ? new Date(recurrence.repeatUntilDate) : null,
        },
        limit: MAX_OCCURRENCE_LIMIT,
        timeZone,
      })
    )
    .map((occurrence) => occurrence.startUtc)
    .sort((first, second) => first.getTime() - second.getTime());

const joinRulesDates = (dates: (string | null)[]): string | null =>
  dates.filter((date): date is string => Boolean(date)).join(", ") || null;

interface SweepstakesRulesDatesSource {
  salesOpenDate?: Date | string | null;
  salesClosedDate?: Date | string | null;
  createdAtUtc?: Date | string | null;
  recurrences?: readonly SweepstakesDrawRecurrence[] | null;
}

export const getSweepstakesRulesDates = (
  { salesOpenDate, salesClosedDate, createdAtUtc, recurrences }: SweepstakesRulesDatesSource,
  timeZone: string
): Pick<
  SweepstakesRulesInput,
  | "entryOpenDatetime"
  | "entryCloseDatetime"
  | "timezoneLabel"
  | "drawingDatetime"
  | "winnerNotificationDate"
  | "winnersListRequestDeadline"
  | "lastUpdateDate"
> => {
  const drawingStarts = getSweepstakesDrawingStarts(recurrences, timeZone);
  const entryCloseDate = parseRulesDate(salesClosedDate) ?? drawingStarts[drawingStarts.length - 1] ?? null;

  return {
    entryOpenDatetime: formatSweepstakesRulesDate(salesOpenDate, timeZone),
    entryCloseDatetime: formatSweepstakesRulesDate(entryCloseDate, timeZone),
    timezoneLabel: timeZone,
    drawingDatetime: joinRulesDates(drawingStarts.map((date) => formatSweepstakesRulesDate(date, timeZone))),
    winnerNotificationDate: joinRulesDates(
      drawingStarts.map((date) => formatSweepstakesRulesDay(addDays(date, 1), timeZone))
    ),
    winnersListRequestDeadline:
      entryCloseDate &&
      formatSweepstakesRulesDay(addDays(entryCloseDate, WINNERS_LIST_REQUEST_DELAY_IN_DAYS), timeZone),
    lastUpdateDate: formatSweepstakesRulesDay(createdAtUtc, timeZone),
  };
};
